import { CloudLocation } from "@/models/microsoft/CloudLocation";
import { combine } from "@/services/microsoft/utils";

export type PickerMode = "file" | "folder";

/**
 * The subset of the Microsoft File Picker v8 configuration schema we use.
 * https://learn.microsoft.com/onedrive/developer/controls/file-pickers/v8-schema
 */
export type FilePickerConfig = {
  sdk: "8.0";
  entry:
    | {
        oneDrive: {
          files?: Record<string, never>;
          folders?: Record<string, never>;
        };
      }
    | {
        sharePoint: {
          byPath: {
            web: string;
            /** Library name relative to the web, e.g. "Shared Documents". */
            list?: string;
            /** Folder path relative to the list, e.g. "foo/bar". */
            folder?: string;
            fallbackToRoot: boolean;
          };
        };
      };
  authentication: Record<string, never>;
  messaging: { origin: string; channelId: string };
  selection: { mode: "single" | "multiple" };
  typesAndSources: {
    mode: "files" | "folders";
    pivots?: { oneDrive: true; recent?: true };
  };
  view: { mode: "files" | "folders" };
  search?: { enabled: boolean };
};

export type BuildFilePickerConfigArgs = {
  pickerMode: PickerMode;
  channelId: string;
  origin: string;
  location: CloudLocation;
};

/**
 * The picker is OneDrive *or* SharePoint per instance. OneDrive opens the user's
 * own drive with the OneDrive pivots (today's behaviour); a site opens that
 * site's libraries by path with search enabled so any site is reachable
 * without depending on the insight-driven "Quick access" list.
 */
export const buildFilePickerConfig = ({
  pickerMode,
  channelId,
  origin,
  location,
}: BuildFilePickerConfigArgs): FilePickerConfig => {
  const listMode = pickerMode === "file" ? "files" : "folders";
  const common = {
    sdk: "8.0" as const,
    authentication: {},
    messaging: { origin, channelId },
    selection: { mode: pickerMode === "file" ? "multiple" : "single" } as const,
    view: { mode: listMode } as const,
  };

  if (location.kind === "oneDrive") {
    return {
      ...common,
      entry: {
        oneDrive: pickerMode === "file" ? { files: {} } : { folders: {} },
      },
      typesAndSources: {
        mode: listMode,
        pivots:
          pickerMode === "file"
            ? { oneDrive: true, recent: true } // File mode: OneDrive and Recent
            : { oneDrive: true }, // Folder mode: start in "My Files"
      },
    };
  }

  return {
    ...common,
    entry: {
      sharePoint: {
        byPath: {
          web: location.webUrl,
          ...(location.list ? { list: location.list } : {}),
          ...(location.folder ? { folder: location.folder } : {}),
          fallbackToRoot: true,
        },
      },
    },
    typesAndSources: { mode: listMode },
    search: { enabled: true },
  };
};

export type PickerEndpoints = {
  /** Audience of the self-issued SharePoint token (a host origin). */
  tokenResource: string;
  /** Web the picker page is POSTed to (`{postBaseUrl}/_layouts/15/FilePicker.aspx`). */
  postBaseUrl: string;
};

/**
 * OneDrive lives on the tenant's `-my` host, derived from the tenant root site
 * URL (Graph `GET /sites/root`). A site carries its own web URL, so no root
 * lookup is needed.
 */
export const resolvePickerEndpoints = (
  location: CloudLocation,
  tenantRootUrl: string | null
): PickerEndpoints | null => {
  if (location.kind === "site") {
    try {
      return {
        tokenResource: new URL(location.webUrl).origin,
        postBaseUrl: location.webUrl,
      };
    } catch {
      // A remembered/searched site with a malformed web URL; caller shows the generic toast.
      return null;
    }
  }

  if (!tenantRootUrl) return null;
  const myHost = tenantRootUrl.replace(".sharepoint.com", "-my.sharepoint.com");
  return { tokenResource: myHost, postBaseUrl: myHost };
};

/**
 * `locale` is optional so the OneDrive URL stays byte-for-byte what it was
 * before the chooser existed.
 */
export const buildFilePickerUrl = (
  postBaseUrl: string,
  config: FilePickerConfig,
  locale?: string
): string => {
  const queryString = new URLSearchParams({
    filePicker: JSON.stringify(config),
    ...(locale ? { locale } : {}),
  });
  return combine(postBaseUrl, `_layouts/15/FilePicker.aspx?${queryString}`);
};
